'use client';

import React from 'react';
import Layout from '../../components/Layout';
import SubDocsLayout from '../../components/SubDocsLayout';
import SEOHead from '../../components/SEOHead';
import useTranslation from 'next-translate/useTranslation';
import { useTheme } from '../../lib/ThemeContext';

export default function PrivacyPolicySubPage() {
  const { lang } = useTranslation('common');
  const { theme } = useTheme();
  const isLt = lang === 'lt';
  const isDarkTheme = theme === 'dark';

  const headingClass = isDarkTheme ? 'text-gray-100' : 'text-gray-900';
  const bodyClass = isDarkTheme ? 'text-gray-300' : 'text-gray-700';

  return (
    <Layout>
      <SEOHead
        title={isLt ? 'Privatumo politika | GadgetFever' : 'Privacy Policy | GadgetFever'}
        description={
          isLt
            ? 'Kaip GadgetFever tvarko asmens duomenis, užsakymų informaciją ir slapukus pagal GDPR bei ES teisę.'
            : 'How GadgetFever processes personal data, order information, and cookies under GDPR and EU law.'
        }
        keywords='privacy policy, gdpr, ecommerce privacy, dropshipping, personal data, gadgetfever'
        canonicalUrl={isLt ? '/lt/sub/privatumo-politika' : '/sub/privacy-policy'}
        lang={lang}
      />

      <SubDocsLayout>
        <h1 className={`text-2xl sm:text-3xl font-semibold mb-4 text-left ${headingClass}`}>
          {isLt ? 'Privatumo politika' : 'Privacy Policy'}
        </h1>

        <p className={`text-base mb-8 text-left leading-relaxed ${bodyClass}`}>
          {isLt
            ? 'GadgetFever saugo jūsų privatumą. Ši politika paaiškina, kokius duomenis renkame, kodėl juos naudojame ir kokias teises turite pagal GDPR.'
            : 'GadgetFever respects your privacy. This policy explains what data we collect, why we use it, and your rights under GDPR.'}
        </p>

        <div className='space-y-8 text-left'>
          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '1. Kokius duomenis renkame' : '1. Data we collect'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Mes galime rinkti jūsų vardą, el. paštą, telefono numerį, pristatymo adresą, užsakymo informaciją ir mokėjimo būdo metaduomenis (pilni kortelės duomenys pas mus nesaugomi).'
                : 'We may collect your name, email, phone number, shipping address, order details, and payment metadata (full card details are not stored by us).'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '2. Kodėl naudojame duomenis' : '2. Why we use data'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Duomenys naudojami užsakymų vykdymui, klientų aptarnavimui, grąžinimams, sukčiavimo prevencijai ir teisinių įsipareigojimų vykdymui.'
                : 'Data is used for order fulfillment, customer support, returns, fraud prevention, and compliance with legal obligations.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '3. Teisinis pagrindas (GDPR)' : '3. Legal basis (GDPR)'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Duomenis tvarkome sutarties vykdymo, teisėtų interesų, teisinės prievolės ir (kai taikoma) sutikimo pagrindu.'
                : 'We process data based on contract performance, legitimate interests, legal obligations, and consent where required.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '4. Duomenų dalinimasis' : '4. Data sharing'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Duomenys gali būti perduodami mokėjimų tiekėjams, logistikos partneriams ir platformos paslaugų teikėjams tik tiek, kiek būtina užsakymo įvykdymui.'
                : 'Data may be shared with payment providers, logistics partners, and platform service providers only as needed to process your order.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '5. Saugojimo terminai' : '5. Data retention'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Duomenis saugome tiek, kiek būtina apskaitos, garantinių ir teisinių reikalavimų vykdymui. Pasibaigus terminams, duomenys ištrinami arba anonimizuojami.'
                : 'We retain data only as long as necessary for accounting, warranty, and legal obligations. After retention periods expire, data is deleted or anonymized.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '6. Jūsų teisės' : '6. Your rights'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Jūs turite teisę susipažinti su duomenimis, juos taisyti, ištrinti, apriboti tvarkymą, perkelti duomenis ir pateikti skundą priežiūros institucijai.'
                : 'You have the right to access, rectify, erase, restrict processing, request portability, and lodge a complaint with a supervisory authority.'}
            </p>
          </section>
        </div>

        <p className={`mt-10 text-sm text-left ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>
          {isLt ? 'Paskutinį kartą atnaujinta:' : 'Last updated:'} {new Date().toLocaleDateString(isLt ? 'lt-LT' : 'en-GB')}
        </p>
      </SubDocsLayout>
    </Layout>
  );
}
